import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  Chip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Grid, Divider, IconButton, Tooltip,
  FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { fetchBrandList } from '../../api/brands';

const FIELD_LABELS = {
  neck_circ:     'Neck',
  bust_circ:     'Bust / Chest',
  waist_circ:    'Waist',
  high_hip_circ: 'High Hip',
  hip_circ:      'Hip',
  shoulder_len:  'Shoulder',
  armhole_depth: 'Armhole Depth',
  front_waist_len: 'Front Waist Length',
  back_waist_len:  'Back Waist Length',
  bust_point_height: 'Bust Point Height',
  back_width:    'Back Width',
  bicep_circ:    'Bicep',
  arm_length:    'Arm Length',
  thigh_circ:    'Thigh',
  knee_circ:     'Knee',
  calf_circ:     'Calf',
  ankle_circ:    'Ankle',
  waist_to_hip:  'Waist to Hip',
  rise:          'Rise',
  front_rise:    'Front Rise',
  back_rise:     'Back Rise',
  crotch_length: 'Crotch Length',
  inseam:        'Inseam',
  outseam:       'Outseam',
  knee_height:   'Knee Height',
};

const MeasurementProfilesPage = () => {
  const [profiles, setProfiles]         = useState([]);
  const [filtered, setFiltered]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [search, setSearch]             = useState('');
  const [selected, setSelected]         = useState(null);
  const [editEmail, setEditEmail]       = useState('');
  const [editName, setEditName]         = useState('');
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState(null);
  const [copied, setCopied]             = useState(false);
  const [brands, setBrands]             = useState([]);
  const [brandId, setBrandId]           = useState('');
  const [brandsLoading, setBrandsLoading] = useState(true);

  const loadBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const res = await fetchBrandList();
      const nextBrands = res?.brands || [];
      setBrands(nextBrands);
      setBrandId((current) => current || nextBrands[0]?._id || '');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load brands');
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (!brandId) {
      setProfiles([]);
      setFiltered([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/measurement-requests/profiles?brandId=${brandId}`);
      const data = res.data?.profiles || [];
      setProfiles(data);
      setFiltered(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { loadBrands(); }, [loadBrands]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      q
        ? profiles.filter(p =>
            p.email?.toLowerCase().includes(q) ||
            p.name?.toLowerCase().includes(q)
          )
        : profiles
    );
  }, [search, profiles]);

  const handleOpen = (profile) => {
    setSelected(profile);
    setEditEmail(profile.email || '');
    setEditName(profile.name || '');
    setSaveError(null);
  };

  const handleClose = () => {
    setSelected(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editEmail.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await axios.patch(`/api/measurement-requests/profiles/${selected._id}`, {
        email: editEmail.trim().toLowerCase(),
        name:  editName.trim(),
      });
      await load();
      handleClose();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const measurementEntries = (m = {}) =>
    Object.entries(m).filter(([, v]) => v != null && v !== '');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Measurement Profiles</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Submitted via measurement request links
          </Typography>
        </Box>
        <Chip label={`${filtered.length} profiles`} variant="outlined" />
      </Box>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="measurement-brand-label">Brand</InputLabel>
        <Select
          labelId="measurement-brand-label"
          label="Brand"
          value={brandId}
          onChange={(event) => setBrandId(event.target.value)}
          disabled={brandsLoading || brands.length === 0}
        >
          {brands.map((brand) => (
            <MenuItem key={brand._id || brand.id} value={brand._id || brand.id}>
              {brand.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        placeholder="Search by name or email…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ),
        }}
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Measurements</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No profiles found
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(p => (
                <TableRow key={p._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpen(p)}>
                  <TableCell>{p.name || <span style={{ color: '#999' }}>—</span>}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {p.email}
                      <Tooltip title={copied ? 'Copied!' : 'Copy email'}>
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); copyEmail(p.email); }}
                        >
                          <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {measurementEntries(p.measurements).slice(0, 4).map(([k, v]) => (
                        <Chip
                          key={k}
                          label={`${FIELD_LABELS[k] || k}: ${v}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {measurementEntries(p.measurements).length > 4 && (
                        <Chip
                          label={`+${measurementEntries(p.measurements).length - 4} more`}
                          size="small"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{p.measurementUnit || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.accountClaimStatus || 'unclaimed'}
                      size="small"
                      color={p.accountClaimStatus === 'claimed' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {p.lastSubmittedAt
                      ? new Date(p.lastSubmittedAt).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleOpen(p); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail / Edit dialog */}
      <Dialog open={!!selected} onClose={handleClose} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Measurement Profile
              <IconButton onClick={handleClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={editEmail}
                    onChange={e => setEditEmail(e.target.value)}
                    helperText="Update to match the customer's portal sign-up email"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    fullWidth
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Measurements ({selected.measurementUnit || 'in'})
              </Typography>
              <Grid container spacing={1}>
                {measurementEntries(selected.measurements).map(([k, v]) => (
                  <Grid item xs={6} sm={4} key={k}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {FIELD_LABELS[k] || k}
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>{v}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSave} variant="contained" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default MeasurementProfilesPage;
